import { useCallback, useMemo } from 'react';
import { Category, Person, Destination } from '../types/budget';
import { useUrlSearchParams } from './useUrlSearchParams';

export interface TransactionFiltersState {
  category: Category | '';
  responsible: Person | '';
  destination: Destination | '';
  amountType: '' | 'income' | 'expense';
  search: string;
}

type SortField = 'category' | 'responsible' | 'description' | 'destination' | 'amount';
type SortDirection = 'asc' | 'desc';

const FILTER_PARAM_KEYS = {
  category: 'cat',
  responsible: 'resp',
  destination: 'dest',
  amountType: 'type',
  search: 'q',
  sortField: 'sort',
  sortDirection: 'dir',
} as const;

export const useTransactionFilters = () => {
  const { getParam, setParam, setMultipleParams } = useUrlSearchParams();

  // Get filters from URL parameters
  const filters: TransactionFiltersState = useMemo(() => ({
    category: getParam(FILTER_PARAM_KEYS.category) as Category | '',
    responsible: getParam(FILTER_PARAM_KEYS.responsible) as Person | '',
    destination: getParam(FILTER_PARAM_KEYS.destination) as Destination | '',
    amountType: getParam(FILTER_PARAM_KEYS.amountType) as '' | 'income' | 'expense',
    search: getParam(FILTER_PARAM_KEYS.search),
  }), [getParam]);

  // Get sort state from URL parameters
  const sortField: SortField = (getParam(FILTER_PARAM_KEYS.sortField) as SortField) || 'amount';
  const sortDirection: SortDirection = (getParam(FILTER_PARAM_KEYS.sortDirection) as SortDirection) || 'desc';

  // Update a single filter
  const setFilter = useCallback((key: keyof TransactionFiltersState, value: string) => {
    setParam(FILTER_PARAM_KEYS[key], value);
  }, [setParam]);

  // Update multiple filters at once
  const setFilters = useCallback((newFilters: Partial<TransactionFiltersState>) => {
    const params: Record<string, string> = {};
    
    Object.entries(newFilters).forEach(([key, value]) => {
      const paramKey = FILTER_PARAM_KEYS[key as keyof TransactionFiltersState];
      if (paramKey) {
        params[paramKey] = value || '';
      }
    });
    
    setMultipleParams(params);
  }, [setMultipleParams]);

  // Update sort parameters
  const setSort = useCallback((field: SortField, direction: SortDirection) => {
    setMultipleParams({
      [FILTER_PARAM_KEYS.sortField]: field,
      [FILTER_PARAM_KEYS.sortDirection]: direction,
    });
  }, [setMultipleParams]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setMultipleParams({
      [FILTER_PARAM_KEYS.category]: '',
      [FILTER_PARAM_KEYS.responsible]: '',
      [FILTER_PARAM_KEYS.destination]: '',
      [FILTER_PARAM_KEYS.amountType]: '',
      [FILTER_PARAM_KEYS.search]: '',
    });
  }, [setMultipleParams]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value !== '');
  }, [filters]);

  return {
    filters,
    setFilter,
    setFilters,
    sortField,
    sortDirection,
    setSort,
    resetFilters,
    hasActiveFilters,
  };
};